import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, KeyRound, Laptop2, Download, CreditCard, Users,
    LifeBuoy, Settings as SettingsIcon, LogOut, ChevronDown, ExternalLink,
    MoonStar, Sun,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { isDesktop, useWindowControlsOverlayInset } from "@/lib/desktopShell";
import { isPortalOnly } from "@/lib/portalOnly";
import EmailVerifyBanner from "@/components/portal/EmailVerifyBanner";
import NotificationBell from "@/components/portal/NotificationBell";

// The Client Portal is a deliberately separate surface from the migration
// dashboard (Layout.jsx / mb-sidebar's dark navy) — see the licensing
// plan's routing section. Light surfaces, same brand blue accent, no
// migration nav items: Overview, Licenses, Devices, Downloads, Billing,
// Team, Support, Settings only.
const PORTAL_NAV = [
    { to: "/portal", label: "Overview", icon: LayoutDashboard, end: true, testId: "portal-nav-overview" },
    { to: "/portal/licenses", label: "Licenses", icon: KeyRound, testId: "portal-nav-licenses" },
    { to: "/portal/devices", label: "Devices", icon: Laptop2, testId: "portal-nav-devices" },
    { to: "/portal/downloads", label: "Downloads", icon: Download, testId: "portal-nav-downloads" },
    { to: "/portal/billing", label: "Billing", icon: CreditCard, testId: "portal-nav-billing", minRole: "admin" },
    { to: "/portal/team", label: "Team", icon: Users, testId: "portal-nav-team" },
    { to: "/portal/support", label: "Support", icon: LifeBuoy, testId: "portal-nav-support" },
    { to: "/portal/settings", label: "Settings", icon: SettingsIcon, testId: "portal-nav-settings" },
];

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

export default function PortalLayout() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    // See Layout.jsx's identical use of this -- keeps the user menu clear
    // of Windows' native title-bar overlay buttons.
    const overlayInset = useWindowControlsOverlayInset();

    const userRank = ROLE_RANK[user?.role] || 0;
    const visibleNav = PORTAL_NAV.filter((i) => !i.minRole || userRank >= (ROLE_RANK[i.minRole] || 0));

    const doLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const initials = (user?.name || user?.email || "U")
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("") || "U";

    return (
        <div className="flex min-h-screen bg-background" data-testid="portal-shell">
            {/* SIDEBAR — light surface, brand-blue active state, distinct
                from the migration dashboard's always-dark mb-sidebar. */}
            <aside
                className="sticky top-0 flex h-screen w-[236px] shrink-0 flex-col border-r border-border bg-surface"
                data-testid="portal-sidebar"
            >
                <div className="flex items-center gap-2 px-5 pb-4 pt-6">
                    <img src="/brand/moovbytes-logo.png" alt="MoovBytes" className="h-7 w-auto" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                        Portal
                    </span>
                </div>

                <nav className="flex-1 overflow-y-auto px-3 pb-3 pt-1">
                    <ul className="space-y-[2px]">
                        {visibleNav.map((item) => {
                            const Icon = item.icon;
                            const active = item.end ? pathname === item.to : pathname.startsWith(item.to);
                            return (
                                <li key={item.to}>
                                    <Link
                                        to={item.to}
                                        data-testid={item.testId}
                                        className={[
                                            "group relative flex items-center gap-3 rounded-md pl-3 pr-2.5 py-[8px] text-[13px] transition-all",
                                            active
                                                ? "bg-gradient-to-r from-brand to-cyan-brand text-white font-semibold shadow-sm"
                                                : "text-foreground/70 hover:bg-muted hover:text-foreground font-medium",
                                        ].join(" ")}
                                    >
                                        <Icon size={17} strokeWidth={active ? 2.3 : 2} />
                                        <span className="flex-1">{item.label}</span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Discoverability escape hatch for existing SaaS customers —
                    /dashboard stays behind ProtectedRoute, so access control
                    is already satisfied; this is purely the visible link.
                    Hidden entirely on a standalone portal-only deployment,
                    where /dashboard doesn't exist — NotOnPortalOnly bounces
                    it straight back to /portal, so the link would just be
                    a broken promise. */}
                {!isDesktop() && !isPortalOnly() && (
                    <div className="border-t border-border px-3 py-3">
                        <Link
                            to="/dashboard"
                            data-testid="portal-open-migration-dashboard"
                            className="flex items-center gap-2 rounded-md px-3 py-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                            <ExternalLink size={13} />
                            Open migration dashboard
                        </Link>
                    </div>
                )}
            </aside>

            <div className="flex min-w-0 flex-1 flex-col">
                <header
                    className="flex h-16 items-center justify-between border-b border-border bg-surface px-6"
                    style={overlayInset ? { paddingRight: `calc(1.5rem + ${overlayInset}px)` } : undefined}
                >
                    <div className="font-display text-lg font-semibold tracking-tight" data-testid="portal-header-title">
                        {visibleNav.find((i) => (i.end ? pathname === i.to : pathname.startsWith(i.to)))?.label || "Overview"}
                    </div>
                    <div className="flex items-center gap-3">
                        <NotificationBell />
                        <button
                            onClick={toggle}
                            data-testid="portal-theme-toggle"
                            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                            className="mb-focus inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                            {theme === "dark" ? <Sun size={15} /> : <MoonStar size={15} />}
                        </button>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setUserMenuOpen((v) => !v)}
                                data-testid="portal-user-btn"
                                className="mb-focus flex items-center gap-2 rounded-full p-0.5 pr-2 transition-colors hover:bg-muted"
                            >
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">
                                    {initials}
                                </div>
                                <div className="hidden leading-tight md:block text-left">
                                    <div className="text-sm font-semibold">{user?.name || "—"}</div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                        {user?.role}
                                    </div>
                                </div>
                                <ChevronDown size={13} className="hidden md:block text-muted-foreground" />
                            </button>
                            {userMenuOpen && (
                                <div
                                    role="menu"
                                    data-testid="portal-user-menu"
                                    className="absolute right-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-lg border border-border bg-surface shadow-2xl"
                                >
                                    <div className="border-b border-border px-3 py-3">
                                        <div className="truncate text-sm font-semibold">{user?.name || "—"}</div>
                                        <div className="truncate text-[11px] text-muted-foreground">{user?.email}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={doLogout}
                                        role="menuitem"
                                        data-testid="portal-logout-btn"
                                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-[hsl(var(--destructive))] transition-colors hover:bg-[hsl(var(--destructive))]/10"
                                    >
                                        <LogOut size={14} /> Log out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                <EmailVerifyBanner user={user} />
                <main className="flex-1 overflow-y-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
